import axios from 'axios';

// Create axios instance with base URL
// Use environment variable for API URL, fallback to same domain as frontend
const API_BASE_URL = import.meta.env.VITE_API_URL || `${window.location.origin}/api`;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to get user ID from Clerk
// This will be set by components that use Clerk
let getCurrentUserId = () => null;

export const setUserIdGetter = (fn) => {
  getCurrentUserId = fn;
};

// Request interceptor for logging and adding user_id
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    
    // Add user_id to headers if available
    const userId = getCurrentUserId();
    if (userId) {
      config.headers['X-User-ID'] = userId;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// =============================================================================
// API Functions
// =============================================================================

// Status
export const getStatus = () => api.get('/status');

// MT5
export const getAccount = () => api.get('/mt5/account');
export const getSymbols = () => api.get('/mt5/symbols');
export const getPrices = () => api.get('/mt5/prices');
export const getCandles = (symbol, timeframe = 'H1', count = 500) =>
  api.get(`/mt5/candles/${symbol}`, { params: { timeframe, count } });
export const getPositions = () => api.get('/mt5/positions');
export const getHistory = (days = 30) => api.get('/mt5/history', { params: { days } });
export const getHistorySignals = () => api.get('/history');

// Analysis
export const analyzeStructure = (symbol) => api.get(`/analysis/structure/${symbol}`);
export const getConfluence = (symbol, direction = null) =>
  api.get(`/analysis/confluence/${symbol}`, { params: direction ? { direction } : {} });
export const scanMarket = (symbols, minScore = 30) =>
  api.post('/analysis/scan', { symbols, min_score: minScore });
export const getPresets = (userId = null) => {
  const config = {};
  if (userId) {
    config.headers = { 'X-User-ID': userId };
  }
  return api.get('/analysis/presets', config);
};

export const setPreset = (preset, userId = null) => {
  const data = { preset };
  if (userId) {
    data.user_id = userId;
  }
  const config = {};
  if (userId) {
    config.headers = { 'X-User-ID': userId };
  }
  return api.post('/analysis/set-preset', data, config);
};

// AI
export const getAiAnalysis = (data) => api.post('/ai/analyze', data);
export const getAiChecklist = (data) => api.post('/ai/checklist', data);
export const getAiDeepAnalysis = (data) => {
  console.log('📤 API Call: POST /ai/deep-analysis');
  console.log('📤 Request body:', JSON.stringify(data, null, 2));
  console.log('📤 Preset in request:', data?.preset);
  console.log('📤 User ID in request:', data?.user_id);
  return api.post('/ai/deep-analysis', data);
};
export const getAdvancedChecklist = (data) => {
  console.log('📤 API Call: POST /ai/advanced-checklist');
  console.log('📤 Request body:', JSON.stringify(data, null, 2));
  console.log('📤 Preset in request:', data?.preset);
  console.log('📤 User ID in request:', data?.user_id);
  return api.post('/ai/advanced-checklist', data);
};

// Trading Mentor
export const getTradingMentor = () => {
  console.log('📤 API Call: POST /ai/trading-mentor');
  return api.post('/ai/trading-mentor', {}, {
    timeout: 180000, // 3 minutes
  });
};

// Setup Timing Advisor
export const getSetupTimingAdvisor = () => {
  console.log('📤 API Call: POST /setup-timing-advisor');
  return api.post('/setup-timing-advisor', {}, {
    timeout: 60000, // 1 minute
  });
};

// Daily Best Trade
export const getDailyBestTrade = (userId = null) => {
  console.log('📤 API Call: GET /daily-best-trade');
  const params = userId ? { user_id: userId } : {};
  return api.get('/daily-best-trade', { params });
};

// Open User Trade
export const openUserTrade = (userId, tradeData) => {
  console.log('📤 API Call: POST /user-trade/open');
  return api.post('/user-trade/open', {
    user_id: userId,
    rank: tradeData.rank,
    custom_levels: {
      entry: tradeData.entry,
      sl: tradeData.stop_loss,
      tp: tradeData.take_profit
    }
  });
};

// User Performance
export const getUserPerformance = (userId = null, days = 7) => {
  console.log('📤 API Call: GET /user-performance');
  const params = {};
  if (userId) params.user_id = userId;
  if (days) params.days = days;
  return api.get('/user-performance', { params });
};

// MT5 Accounts
export const getMT5Accounts = (userId) => api.get(`/mt5/accounts/${userId}`);
export const addMT5Account = (data) => api.post('/mt5/accounts', data);
export const syncMT5Account = (connectionId) => api.post(`/mt5/accounts/${connectionId}/sync`);
export const toggleMT5Account = (connectionId) => api.patch(`/mt5/accounts/${connectionId}/toggle`);
export const removeMT5Account = (connectionId) => api.delete(`/mt5/accounts/${connectionId}`);

// Journal
export const getJournalStats = () => api.get('/journal/stats');
export const getTrades = (params) => api.get('/journal/trades', { params });
export const addTrade = (data) => api.post('/journal/trade', data);
export const updateTrade = (id, data) => api.put(`/journal/trade/${id}`, data);
export const getCalendar = (year, month) =>
  api.get('/journal/calendar', { params: { year, month } });
export const getWeeklySummary = () => api.get('/journal/weekly');
export const getStatsBySymbol = () => api.get('/journal/by-symbol');

// Sync
export const syncMt5Trades = () => api.post('/sync/mt5-to-journal');

// Scanner
export const getScannerLatest = (preset = null, userId = null) => {
  const params = {};
  if (preset) {
    params.preset = preset;
  }
  if (userId) {
    params.user_id = userId;
  }
  const config = {};
  if (userId) {
    config.headers = { 'X-User-ID': userId };
  }
  return api.get('/scanner/latest', { params, ...config });
};

// Signals
export const getSignalsHistory = (params = {}) => api.get('/signals/history', { params });
export const getSignalsActive = () => api.get('/signals/active');
export const getSignalsPerformance = () => api.get('/signals/performance');

// Setups
export const getSetupsForHistory = () => api.get('/setups/for-history');

// Push Notifications
export const subscribePushNotifications = (userId, subscription) =>
  api.post('/push/subscribe', { user_id: userId, subscription });
export const unsubscribePushNotifications = (userId) =>
  api.post('/push/unsubscribe', { user_id: userId });
export const checkPushSubscription = (userId) =>
  api.get(`/push/subscription/${userId}`);

export default api;
